/**
 * A picture on the card.
 *
 * Two things make this more than a Konva.Image with a source. First, fit: a
 * customer drags a box to the shape they want and the picture fills it, which
 * means cropping to the box rather than stretching to it - a squashed logo is
 * the most common way a cheap designer looks cheap. Second, filters run through
 * Konva's own cache, because filtering per frame on a 20 MP photo is not
 * something a mid-range Android can do at 60 fps.
 */

import { useEffect, useRef, useState } from 'react';
import { Group, Image as KonvaImage, Rect, Shape, Text } from 'react-konva';
import Konva from 'konva';
import type { ImageLayer } from '../model/types';
import { mmToPx } from '../model/units';
import { imageFor, knownAsset, onAssetsChanged } from '../assets';
import { onFontsChanged } from '../text/fonts';
import { canvasMask, drawMasked, filterCss, sourceRect } from './mask';

interface Props {
  layer: ImageLayer;
  zoom: number;
}

export function ImageNode({ layer, zoom }: Props) {
  const node = useRef<Konva.Image>(null);
  const [, redraw] = useState(0);

  // A picture arriving asynchronously has to reach the canvas somehow.
  useEffect(() => onAssetsChanged(() => redraw((n) => n + 1)), []);

  // A text used as a mask needs its font.
  useEffect(() => onFontsChanged(() => redraw((n) => n + 1)), []);

  const width = mmToPx(layer.w, zoom);
  const height = mmToPx(layer.h, zoom);
  const picture = layer.asset ? imageFor(layer.asset) : null;

  const { brightness, contrast, saturation, greyscale } = layer.filters;
  const filtered = brightness !== 0 || contrast !== 0 || saturation !== 0 || greyscale;

  /**
   * Filters need the node cached, and the cache needs redoing whenever the
   * picture, its size or the settings change. Leaving a stale cache in place is
   * how a filter appears to do nothing.
   */
  useEffect(() => {
    const image = node.current;

    if (!image || !picture) return;

    if (!filtered) {
      image.clearCache();
      image.filters([]);
      image.getLayer()?.batchDraw();

      return;
    }

    image.filters([Konva.Filters.Brighten, Konva.Filters.Contrast, Konva.Filters.HSL]);
    image.brightness(brightness);
    image.contrast(contrast * 100);
    // Greyscale is saturation taken all the way down, which keeps one filter
    // doing one job instead of two that can disagree.
    image.saturation(greyscale ? -2 : saturation * 2);

    image.cache();
    image.getLayer()?.batchDraw();
  }, [picture, filtered, brightness, contrast, saturation, greyscale, width, height]);

  const mask = canvasMask(layer);

  if (mask && mask !== 'waiting' && (layer.mask?.layer || picture)) {
    return <MaskedPicture layer={layer} picture={picture} mask={mask} width={width} height={height} zoom={zoom} />;
  }

  if (!picture) {
    return <Waiting width={width} height={height} missing={!layer.asset || !knownAsset(layer.asset)} />;
  }

  const crop = cropFor(layer, picture.naturalWidth, picture.naturalHeight);

  return (
    <KonvaImage
      ref={node}
      image={picture}
      width={width}
      height={height}
      crop={crop}
      // Konva reads the cache at the node's own scale, so a cached image at a
      // deep zoom would be soft. One extra pixel ratio is enough for the zoom
      // levels a card is edited at, and cheap.
      imageSmoothingEnabled
    />
  );
}

/**
 * A picture seen through its mask - or, with no picture yet, the empty frame.
 *
 * Drawn by hand rather than as a Konva.Image, because the mask is a clip or a
 * composited alpha that Konva has no node for. The picture's adjustments are
 * approximated with canvas filters here; the frame is exact.
 */
function MaskedPicture({
  layer,
  picture,
  mask,
  width,
  height,
  zoom,
}: {
  layer: ImageLayer;
  picture: HTMLImageElement | null;
  mask: Exclude<ReturnType<typeof canvasMask>, null | 'waiting'>;
  width: number;
  height: number;
  zoom: number;
}) {
  const pxPerMm = mmToPx(1, zoom);
  const empty = !picture;
  const missing = !layer.asset || !knownAsset(layer.asset);

  return (
    <Shape
      width={width}
      height={height}
      sceneFunc={(context, shape) => {
        const ctx = context._context;

        ctx.save();
        ctx.scale(pxPerMm, pxPerMm);

        if (empty) {
          // An empty frame: its shape, tinted and dashed, asking for a picture.
          ctx.fillStyle = 'rgba(133, 136, 142, 0.18)';
          ctx.fill(mask.path, mask.evenOdd ? 'evenodd' : 'nonzero');
          ctx.strokeStyle = '#6f737a';
          ctx.lineWidth = 1.25 / pxPerMm;
          ctx.setLineDash([5 / pxPerMm, 4 / pxPerMm]);
          ctx.stroke(mask.path);
          ctx.setLineDash([]);
          ctx.fillStyle = '#5c6067';
          ctx.font = `${Math.max(9, Math.min(13, height / 4)) / pxPerMm}px system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(missing ? 'Add a picture' : 'Loading…', layer.w / 2, layer.h / 2);
        } else {
          drawMasked(ctx, mask, { w: layer.w, h: layer.h }, (c) => {
            const src = sourceRect(layer, { w: layer.w, h: layer.h }, { w: picture.naturalWidth, h: picture.naturalHeight });
            const filter = filterCss(layer.filters);

            c.save();
            if (filter !== 'none') c.filter = filter;
            // The picture is mirrored with the layer; the mask was already un-mirrored.
            c.drawImage(picture, src.x, src.y, src.w, src.h, 0, 0, layer.w, layer.h);
            c.restore();
          });
        }

        ctx.restore();

        context.beginPath();
        context.rect(0, 0, shape.width(), shape.height());
        context.closePath();
      }}
      hitFunc={(context, shape) => {
        context.beginPath();
        context.rect(0, 0, shape.width(), shape.height());
        context.closePath();
        context.fillStrokeShape(shape);
      }}
    />
  );
}

/**
 * Which part of the source lands in the box.
 *
 * 'fill' stretches, and is offered because occasionally a customer genuinely
 * wants it. 'cover' crops the long side away; 'contain' fits the whole picture
 * inside and is the honest choice for a logo.
 */
function cropFor(layer: ImageLayer, sourceW: number, sourceH: number) {
  // The layer's own crop is a proportion of the source, applied first, so a
  // customer who has cropped a photo keeps that crop when they change the fit.
  const region = {
    x: layer.crop.x * sourceW,
    y: layer.crop.y * sourceH,
    width: Math.max(1, layer.crop.w * sourceW),
    height: Math.max(1, layer.crop.h * sourceH),
  };

  if (layer.fit === 'fill' || layer.w <= 0 || layer.h <= 0) return region;

  const boxRatio = layer.w / layer.h;
  const imageRatio = region.width / region.height;

  if (layer.fit === 'cover') {
    if (imageRatio > boxRatio) {
      // Wider than the box: take a full-height slice from the middle.
      const width = region.height * boxRatio;

      return { ...region, x: region.x + (region.width - width) / 2, width };
    }

    const height = region.width / boxRatio;

    return { ...region, y: region.y + (region.height - height) / 2, height };
  }

  // 'contain' shows everything, which means letterboxing inside the box. Konva
  // has no letterbox, so the crop is widened to the box's shape - the added
  // area is transparent source, which is what a letterbox looks like.
  if (imageRatio > boxRatio) {
    const height = region.width / boxRatio;

    return { ...region, y: region.y - (height - region.height) / 2, height };
  }

  const width = region.height * boxRatio;

  return { ...region, x: region.x - (width - region.width) / 2, width };
}

/**
 * The frame shown before a picture arrives, or when there is none.
 *
 * It says which of the two it is, because "still loading" and "this is empty"
 * ask completely different things of the customer.
 */
function Waiting({ width, height, missing }: { width: number; height: number; missing: boolean }) {
  return (
    <Group>
      <Rect
        width={width}
        height={height}
        fill="rgba(133, 136, 142, 0.14)"
        stroke="#6f737a"
        strokeWidth={1.25}
        dash={[5, 4]}
        strokeScaleEnabled={false}
      />
      <Text
        width={width}
        height={height}
        text={missing ? 'Picture' : 'Loading…'}
        align="center"
        verticalAlign="middle"
        fill="#5c6067"
        fontSize={Math.max(9, Math.min(13, height / 4))}
        fontFamily="system-ui, sans-serif"
        listening={false}
      />
    </Group>
  );
}
